/* eslint-disable */
/**
 * POSServiceManagement - Peace of Sleep Service Management
 * Manages service bookings with List, Card, and Matrix views
 * Migrated to use DataTable component with global CSS styling.
 * Matrix view is bespoke - kept as-is for Phase 2 improvements.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { formatDate, formatMonthShort } from '../../../utils/formatters';
import { useAuth } from '../../../contexts/AuthContext';
import { usePOS } from '../../../contexts/POSContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorDisplay from '../../../components/ErrorDisplay';
import EmptyState from '../../../components/EmptyState';
import DataTable, { StatusBadge, Column } from '../../../components/DataTable';
import SearchSelect from '../../../components/SearchSelect';

// POS Brand Colors (for future theming)
// POS_GOLD imported from posRoomStatus

// Permission constants
const PERMISSIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  CONFIRM: 'confirm',
  COMPLETE: 'complete'
};

import {
  REQUEST_STATUS_CONFIG as STATUS_CONFIG,
  MATRIX_STATUS_CONFIG as SERVICE_STATUS_CONFIG,
  TIER_FREQUENCY as TIER_FREQUENCIES,
  POS_GOLD,
  POS_GOLD_DARK,
} from '../../../config/posRoomStatus';

// Get week number
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function POSServiceManagement() {
  const { supabase, hasPermission, user } = useAuth();
  const {
    selectedPropertyId,
    setSelectedPropertyId,
    userProperties,
    propertiesLoading,
    isCMBStaff,
    isHotelManager,
    canRequestService: contextCanRequest,
    canCancelService,
  } = usePOS();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('pos_services');

  // Permission checks
  const canCreate = isCMBStaff ? hasPermission('pos_services', PERMISSIONS.CREATE) : contextCanRequest;
  const canConfirm = isCMBStaff ? hasPermission('pos_services', PERMISSIONS.CONFIRM) : false;
  const canComplete = isCMBStaff ? hasPermission('pos_services', PERMISSIONS.COMPLETE) : false;
  const canCancel = isCMBStaff ? true : canCancelService;

  // State
  const [serviceRequests, setServiceRequests] = useState<Record<string, any>[]>([]);
  const [rooms, setRooms] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: string } | null>(null);

  // Modal state
  const [viewingRequest, setViewingRequest] = useState<Record<string, any> | null>(null);
  const [showNewBookingModal, setShowNewBookingModal] = useState(false);

  // View mode (list, card, matrix)
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('posServicesViewMode');
    // Migrate 'board' to 'card' for consistency
    return saved === 'board' ? 'card' : (saved || 'list');
  });

  // Matrix state
  const [matrixWeekOffset, setMatrixWeekOffset] = useState(0);
  const [numWeeks, setNumWeeks] = useState(20);

  // New booking form state
  const [bookingForm, setBookingForm] = useState<{
    property_id: string;
    room_ids: string[];
    requested_date: string;
    time_slot: string;
    notes: string;
  }>({
    property_id: '',
    room_ids: [],
    requested_date: '',
    time_slot: '',
    notes: ''
  });

  // Multi-room selector state
  const [showRoomSelector, setShowRoomSelector] = useState(false);
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set());
  const [roomFilterStatus, setRoomFilterStatus] = useState('all');

  // Show toast notification
  const showToast = useCallback((message: string, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('posServicesViewMode', viewMode);
  }, [viewMode]);

  // Calculate number of weeks to fill screen
  useEffect(() => {
    const calculateWeeks = () => {
      const sidebarWidth = 200;
      const pagePadding = 40;
      const cardPadding = 28;
      const fixedColWidth = 100;
      const cellWidth = 28;
      const availableWidth = window.innerWidth - sidebarWidth - pagePadding - cardPadding - fixedColWidth;
      const calculatedWeeks = Math.max(12, Math.floor(availableWidth / cellWidth));
      setNumWeeks(calculatedWeeks);
    };

    calculateWeeks();
    window.addEventListener('resize', calculateWeeks);
    return () => window.removeEventListener('resize', calculateWeeks);
  }, [viewMode]);

  // Load when context is ready
  useEffect(() => {
    if (!permissionLoading && canAccess && !propertiesLoading) {
      setLoading(false);
    }
  }, [permissionLoading, canAccess, propertiesLoading]);

  // Load data when property changes
  useEffect(() => {
    if (selectedPropertyId) {
      loadServiceRequests();
      loadRooms();
    } else {
      setServiceRequests([]);
      setRooms([]);
    }
  }, [selectedPropertyId]);

  const getProperty = () => userProperties.find(p => p.id === selectedPropertyId);

  const loadServiceRequests = async () => {
    if (!selectedPropertyId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('hosp_service_requests')
        .select('*')
        .eq('property_id', selectedPropertyId)
        .order('requested_date', { ascending: false })
        .limit(10000);

      if (error) throw error;

      // Enrich data for DataTable
      const enriched = (data || []).map(req => ({
        ...req,
        requestedDate: req.requested_date ? formatDate(req.requested_date) : '-',
        timeSlotLabel: req.time_slot || '-'
      }));

      setServiceRequests(enriched);
      setLoading(false);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const loadRooms = async () => {
    if (!selectedPropertyId) return;

    try {
      const { data: roomsData, error: roomsError } = await supabase
        .from('hosp_rooms')
        .select('*')
        .eq('property_id', selectedPropertyId)
        .eq('status', 'active')
        .order('room_number')
        .limit(10000);

      if (roomsError) throw roomsError;

      // Load service dates from cert_submissions + hosp_service_history
      const { data: certData } = await supabase
        .from('cert_submissions')
        .select('room_id, service_date')
        .eq('property_id', selectedPropertyId)
        .eq('submission_status', 'certified')
        .order('service_date', { ascending: false })
        .limit(10000);

      const { data: historyData } = await supabase
        .from('hosp_service_history')
        .select('room_id, service_date')
        .eq('property_id', selectedPropertyId)
        .order('service_date', { ascending: false })
        .limit(10000);

      // Create map of room_id -> last service date
      const lastServiceMap: Record<string, string> = {};
      if (historyData) {
        historyData.forEach((h: Record<string, any>) => {
          if (!lastServiceMap[h.room_id]) lastServiceMap[h.room_id] = h.service_date;
        });
      }
      if (certData) {
        certData.forEach((c: Record<string, any>) => {
          if (!lastServiceMap[c.room_id] || c.service_date > (lastServiceMap[c.room_id] as string)) {
            lastServiceMap[c.room_id] = c.service_date;
          }
        });
      }

      const roomsWithService = (roomsData || []).map((room: Record<string, any>) => ({
        ...room,
        last_service_date: lastServiceMap[room.id] || null
      }));

      setRooms(roomsWithService);
    } catch (_err) {
      console.error('Error loading rooms:', _err);
    }
  };

  // Get room numbers for display
  const getRoomNumbers = (roomIds: string[]): string => {
    if (!roomIds || !Array.isArray(roomIds)) return 'No rooms';
    return roomIds.map((id: string) => {
      const room = rooms.find((r: Record<string, any>) => r.id === id);
      return room?.room_number || id;
    }).join(', ');
  };

  // Calculate room status for TODAY
  const getRoomCurrentStatus = (room: Record<string, any>): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const property = getProperty();
    const tier = property?.hospitality_tier || 'assured';
    const frequency = TIER_FREQUENCIES[tier] || 365;
    const dueSoonThreshold = 30;
    const gracePeriodDays = 30;

    const booking = serviceRequests.find(r =>
      r.room_ids?.includes(room.id) && ['pending', 'confirmed'].includes(r.status)
    );
    if (booking) return 'booked';

    const serviceDate = room.last_service_date ? new Date(room.last_service_date) : null;
    if (!serviceDate) return 'pending';

    const dueDate = new Date(serviceDate);
    dueDate.setDate(dueDate.getDate() + frequency);

    const dueSoonDate = new Date(dueDate);
    dueSoonDate.setDate(dueSoonDate.getDate() - dueSoonThreshold);

    const overdueDate = new Date(dueDate);
    overdueDate.setDate(overdueDate.getDate() + gracePeriodDays);

    if (today > overdueDate) return 'overdue';
    if (today >= dueSoonDate) return 'due-soon';
    return 'certified';
  };

  // Filtered rooms for selector
  const filteredRoomsForSelector = useMemo(() => {
    return rooms.filter(room => {
      const status = getRoomCurrentStatus(room);
      if (status === 'booked') return false;
      if (roomFilterStatus === 'all') return true;
      return status === roomFilterStatus;
    });
  }, [rooms, roomFilterStatus, serviceRequests]);

  // Selected rooms totals
  const selectedRoomsTotals = useMemo(() => {
    const selectedRooms = rooms.filter(r => selectedRoomIds.has(r.id));
    return {
      rooms: selectedRooms.length,
      beds: selectedRooms.reduce((sum, r) => sum + (r.bed_count || 1), 0)
    };
  }, [rooms, selectedRoomIds]);

  // Status counts for Card view
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.keys(STATUS_CONFIG).forEach(status => {
      counts[status] = serviceRequests.filter((r: Record<string, any>) => r.status === status).length;
    });
    return counts;
  }, [serviceRequests]);

  // Filter options for DataTable
  const statusOptions = useMemo(() => [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ], []);

  // DataTable columns
  const columns: Column<Record<string, any>>[] = [
    {
      key: 'requestedDate',
      label: 'Date',
      sortable: true,
      render: (row: Record<string, any>) => <span className="fw-500">{row.requestedDate}</span>
    },
    {
      key: 'room_ids',
      label: 'Rooms',
      render: (row: Record<string, any>) => getRoomNumbers(row.room_ids)
    },
    {
      key: 'timeSlotLabel',
      label: 'Time Slot',
      sortable: true
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center' as const,
      sortable: true,
      render: (row: Record<string, any>) => {
        const config = STATUS_CONFIG[row.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;
        return <StatusBadge bg={config.bg} color={config.color}>{config.label}</StatusBadge>;
      }
    },
    {
      key: 'notes',
      label: 'Notes',
      render: (row: Record<string, any>) => row.notes || '-'
    }
  ];

  // Handle status update
  const handleStatusUpdate = async (requestId: string, newStatus: string) => {
    try {
      const now = new Date().toISOString();
      // Use local date to avoid UTC date-shift (e.g. UTC+2 at 11pm would give tomorrow in UTC)
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      const updateData: Record<string, any> = { status: newStatus, updated_at: now };

      if (newStatus === 'confirmed') {
        updateData.confirmed_at = now;
        updateData.confirmed_by = user?.id;
      } else if (newStatus === 'completed') {
        updateData.completed_at = now;
      }

      const { error: reqError } = await supabase
        .from('hosp_service_requests')
        .update(updateData)
        .eq('id', requestId);

      if (reqError) throw reqError;

      // If completing, update room service dates + create history
      if (newStatus === 'completed') {
        const request = serviceRequests.find((r: Record<string, any>) => r.id === requestId);
        if (request && request.room_ids?.length > 0) {
          const { error: roomErr } = await supabase
            .from('hosp_rooms')
            .update({ last_service_date: today, updated_at: now })
            .in('id', request.room_ids);

          if (roomErr) {
            showToast('Warning: failed to update room service dates', 'error');
          }

          const historyEntries = request.room_ids.map((roomId: string) => ({
            property_id: request.property_id,
            room_id: roomId,
            service_date: today,
            service_type: 'standard',
            notes: request.notes || null
          }));

          const { error: histErr } = await supabase.from('hosp_service_history').insert(historyEntries);
          if (histErr) {
            showToast('Warning: failed to record service history', 'error');
          }
        }
      }

      showToast(`Request ${newStatus}`, 'success');
      loadServiceRequests();
    } catch (_err) {
      showToast('Failed to update status', 'error');
    }
  };

  // Room selector handlers
  const toggleRoomSelection = (roomId: string) => {
    setSelectedRoomIds(prev => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  };

  const openRoomSelector = () => {
    setSelectedRoomIds(new Set());
    setRoomFilterStatus('all');
    setShowRoomSelector(true);
  };

  const proceedToBooking = () => {
    if (selectedRoomIds.size === 0) {
      showToast('Please select at least one room', 'error');
      return;
    }
    setBookingForm(prev => ({
      ...prev,
      property_id: selectedPropertyId,
      room_ids: Array.from(selectedRoomIds)
    }));
    setShowRoomSelector(false);
    setShowNewBookingModal(true);
  };

  // Create new booking
  const handleCreateBooking = async () => {
    if (!bookingForm.property_id || bookingForm.room_ids.length === 0 || !bookingForm.requested_date) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showToast('Session expired. Please refresh the page.', 'error');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pos-request-service`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            property_id: bookingForm.property_id,
            room_ids: bookingForm.room_ids,
            requested_date: bookingForm.requested_date,
            time_slot: bookingForm.time_slot || null,
            notes: bookingForm.notes || null
          })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create booking');
      }

      const notifMsg = result.notification_created ? 'CMB has been notified.' : '';
      showToast(`Booking created successfully. ${notifMsg}`, 'success');

      setShowNewBookingModal(false);
      setBookingForm({ property_id: '', room_ids: [], requested_date: '', time_slot: '', notes: '' });
      setSelectedRoomIds(new Set());
      loadServiceRequests();
    } catch (err: any) {
      showToast(err.message || 'Failed to create booking', 'error');
    }
  };

  // Calculate room status for matrix (kept as-is for Phase 2)
  const calculateRoomStatus = (room: Record<string, any>, checkDate: Date): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cellDate = new Date(checkDate);
    cellDate.setHours(0, 0, 0, 0);

    const property = getProperty();
    const tier = property?.hospitality_tier || 'assured';
    const frequency = TIER_FREQUENCIES[tier] || 365;
    const dueSoonThreshold = 30;
    const gracePeriodDays = 30;

    const serviceDate = room.last_service_date ? new Date(room.last_service_date) : null;
    if (serviceDate) serviceDate.setHours(0, 0, 0, 0);

    let dueDate = null;
    if (serviceDate) {
      dueDate = new Date(serviceDate);
      dueDate.setDate(dueDate.getDate() + frequency);
    }

    let dueSoonDate = null;
    let overdueDate = null;
    if (dueDate) {
      dueSoonDate = new Date(dueDate);
      dueSoonDate.setDate(dueSoonDate.getDate() - dueSoonThreshold);
      overdueDate = new Date(dueDate);
      overdueDate.setDate(overdueDate.getDate() + gracePeriodDays);
    }

    const booking = serviceRequests.find(r =>
      r.room_ids?.includes(room.id) && ['pending', 'confirmed'].includes(r.status)
    );

    if (booking) {
      const bookingDate = new Date(booking.requested_date);
      bookingDate.setHours(0, 0, 0, 0);
      const bookingWeekStart = new Date(bookingDate);
      bookingWeekStart.setDate(bookingWeekStart.getDate() - bookingWeekStart.getDay());
      const bookingWeekEnd = new Date(bookingWeekStart);
      bookingWeekEnd.setDate(bookingWeekEnd.getDate() + 6);

      if (cellDate >= bookingWeekStart && cellDate <= bookingWeekEnd) return 'booked';

      if (cellDate > bookingDate) {
        const projectedDueDate = new Date(bookingDate);
        projectedDueDate.setDate(projectedDueDate.getDate() + frequency);
        const projectedDueSoonDate = new Date(projectedDueDate);
        projectedDueSoonDate.setDate(projectedDueSoonDate.getDate() - dueSoonThreshold);
        const projectedOverdueDate = new Date(projectedDueDate);
        projectedOverdueDate.setDate(projectedOverdueDate.getDate() + gracePeriodDays);

        if (cellDate >= bookingDate && cellDate < projectedDueSoonDate) return 'certified';
        if (cellDate >= projectedDueSoonDate && cellDate <= projectedOverdueDate) return 'due-soon';
        if (cellDate > projectedOverdueDate) return 'overdue';
        return 'future';
      }
    }

    if (serviceDate && dueDate && dueSoonDate && overdueDate) {
      if (cellDate >= serviceDate && cellDate < dueSoonDate) return 'certified';
      if (cellDate >= dueSoonDate && cellDate <= overdueDate) return 'due-soon';
      if (cellDate > overdueDate) return 'overdue';
      if (cellDate < serviceDate) return 'pending';
    }

    const contractStart = (property as any)?.contract_start_date ? new Date((property as any).contract_start_date) : null;
    const isContractActive = property?.contract_status === 'active' || property?.contract_status === 'signed';

    if (!serviceDate && contractStart && isContractActive) {
      const firstDueDate = new Date(contractStart);
      firstDueDate.setDate(firstDueDate.getDate() + 30);
      const firstDueSoonDate = new Date(firstDueDate);
      firstDueSoonDate.setDate(firstDueSoonDate.getDate() - dueSoonThreshold);
      const firstOverdueDate = new Date(firstDueDate);
      firstOverdueDate.setDate(firstOverdueDate.getDate() + gracePeriodDays);

      if (cellDate >= firstDueSoonDate && cellDate <= firstOverdueDate) return 'due-soon';
      if (cellDate > firstOverdueDate) return 'overdue';
      if (cellDate < firstDueSoonDate) return cellDate <= today ? 'pending' : 'future';
    }

    if (!serviceDate) return cellDate <= today ? 'pending' : 'future';
    return cellDate > today ? 'future' : 'pending';
  };

  // Permission check
  if (permissionLoading) return <LoadingSpinner />;
  if (!canAccess) return <AccessDenied feature="pos_services" />;
  if (error) return <ErrorDisplay error={error} onRetry={() => { loadServiceRequests(); }} />;

  // Render Card view (kanban-style)
  const renderCardView = () => {
    const cardColumns = ['pending', 'confirmed', 'completed'] as const;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', minHeight: '400px' }}>
        {cardColumns.map(status => (
          <div key={status} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="flex-between" style={{
              padding: '0.75rem 1rem',
              borderTop: `4px solid ${STATUS_CONFIG[status]?.color}`,
              background: 'var(--color-bg-secondary)'
            }}>
              <span className="fw-600">{STATUS_CONFIG[status]?.label}</span>
              <span className="fw-600" style={{
                background: STATUS_CONFIG[status]?.bg,
                color: STATUS_CONFIG[status]?.color,
                padding: '2px 8px',
                borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--text-sm)'
              }}>
                {statusCounts[status] || 0}
              </span>
            </div>
            <div style={{ padding: '0.5rem', maxHeight: '500px', overflowY: 'auto' }}>
              {serviceRequests.filter(r => r.status === status).length === 0 ? (
                <EmptyState message={`No ${STATUS_CONFIG[status]?.label.toLowerCase()} requests`} />
              ) : (
                serviceRequests.filter(r => r.status === status).map(request => (
                  <div
                    key={request.id}
                    onClick={() => setViewingRequest(request)}
                    style={{
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      background: 'var(--color-white)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer'
                    }}
                  >
                    <div className="fw-600 mb-1">
                      {request.requestedDate}
                    </div>
                    <div className="text-xs-gray">
                      Rooms: {getRoomNumbers(request.room_ids)}
                    </div>
                    {request.time_slot && (
                      <div className="text-xs-gray">
                        {request.time_slot}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render Matrix view (kept as-is for Phase 2)
  const renderMatrixView = () => {
    const today = new Date();
    const currentWeek = getWeekNumber(today);
    const currentYear = today.getFullYear();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() + (matrixWeekOffset * 7) - 14);

    const sortedRooms = [...rooms].sort((a, b) =>
      a.room_number.localeCompare(b.room_number, undefined, { numeric: true })
    );

    if (rooms.length === 0) {
      return (
        <div className="card">
          <EmptyState icon="🗓️" message="No rooms found for this property" />
        </div>
      );
    }

    return (
      <div className="card" style={{ padding: '14px', overflow: 'hidden' }}>
        {/* Matrix Navigation */}
        <div className="flex-center flex-wrap" style={{ gap: '6px', marginBottom: '12px' }}>
          <button className="btn btn-secondary" style={{ padding: '5px 8px', fontSize: 'var(--text-xs)' }} onClick={() => setMatrixWeekOffset(o => o - 52)}>◀◀</button>
          <button className="btn btn-secondary" style={{ padding: '5px 8px', fontSize: 'var(--text-xs)' }} onClick={() => setMatrixWeekOffset(o => o - 4)}>◀ 4 wks</button>
          <button className="btn btn-primary" style={{ padding: '5px 8px', fontSize: 'var(--text-xs)', background: POS_GOLD, border: 'none' }} onClick={() => setMatrixWeekOffset(0)}>Today</button>
          <button className="btn btn-secondary" style={{ padding: '5px 8px', fontSize: 'var(--text-xs)' }} onClick={() => setMatrixWeekOffset(o => o + 4)}>4 wks ▶</button>
          <button className="btn btn-secondary" style={{ padding: '5px 8px', fontSize: 'var(--text-xs)' }} onClick={() => setMatrixWeekOffset(o => o + 52)}>▶▶</button>
        </div>

        {/* Matrix Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: 'auto' }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--color-bg)', zIndex: 3, padding: '4px 6px', textAlign: 'left', fontWeight: 600, fontSize: 'var(--text-2xs)', color: 'var(--color-text-light)', borderRight: '1px solid #e2e8f0', minWidth: '50px' }}>Room</th>
                <th style={{ position: 'sticky', left: '50px', padding: '4px 6px', textAlign: 'center', fontWeight: 600, fontSize: 'var(--text-2xs)', color: 'var(--color-text-light)', background: 'var(--color-bg)', zIndex: 3, minWidth: '40px', borderRight: '1px solid #e2e8f0' }}>Beds</th>
                {Array.from({ length: numWeeks }, (_, i) => {
                  const weekDate = new Date(startDate);
                  weekDate.setDate(weekDate.getDate() + (i * 7));
                  const weekNum = getWeekNumber(weekDate);
                  const weekYear = weekDate.getFullYear();
                  const isCurrentWeek = weekNum === currentWeek && weekYear === currentYear;
                  const monthName = formatMonthShort(weekDate);

                  return (
                    <th key={i} style={{
                      padding: '4px 2px',
                      textAlign: 'center',
                      background: isCurrentWeek ? `${POS_GOLD}25` : 'var(--color-bg)',
                      fontWeight: 600,
                      fontSize: 'var(--text-2xs)',
                      color: 'var(--color-text-light)',
                      minWidth: '28px',
                      maxWidth: '28px'
                    }}>
                      <div>W{weekNum}</div>
                      <div style={{ fontSize: '8px', color: 'var(--color-text-light)' }}>{monthName}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRooms.map(room => (
                <tr key={room.id}>
                  <td style={{ position: 'sticky', left: 0, background: 'var(--color-white)', zIndex: 2, padding: '4px 6px', fontWeight: 500, fontSize: 'var(--text-xs)', borderRight: '1px solid #e2e8f0', minWidth: '50px' }}>{room.room_number}</td>
                  <td style={{ position: 'sticky', left: '50px', padding: '4px 6px', textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', background: 'var(--color-white)', zIndex: 2, minWidth: '40px', borderRight: '1px solid #e2e8f0' }}>{room.bed_count || 1}</td>
                  {Array.from({ length: numWeeks }, (_, i) => {
                    const weekDate = new Date(startDate);
                    weekDate.setDate(weekDate.getDate() + (i * 7));
                    const status = calculateRoomStatus(room, weekDate);

                    return (
                      <td key={i} style={{ padding: '1px', textAlign: 'center', width: '28px', minWidth: '28px', maxWidth: '28px' }}>
                        <div
                          style={{
                            width: '24px',
                            height: '20px',
                            borderRadius: 'var(--radius-sm)',
                            margin: '0 auto',
                            background: SERVICE_STATUS_CONFIG[status]?.color || 'var(--color-border)',
                            cursor: 'pointer'
                          }}
                          title={`Room ${room.room_number} - ${SERVICE_STATUS_CONFIG[status]?.label}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="flex-center flex-wrap" style={{ gap: '12px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--color-border)' }}>
          {Object.entries(SERVICE_STATUS_CONFIG).map(([status, config]) => (
            <div key={status} className="flex items-center" style={{ gap: '4px', fontSize: 'var(--text-2xs)', color: 'var(--color-text-light)' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: 'var(--radius-sm)', background: config.color }} />
              {config.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className="fw-500 rounded-lg fixed" style={{
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          background: toast.type === 'error' ? 'var(--color-error-bg)' : toast.type === 'success' ? 'var(--color-success-bg)' : 'var(--color-info-bg)',
          color: toast.type === 'error' ? 'var(--color-error)' : toast.type === 'success' ? 'var(--color-success)' : 'var(--color-info)'
        }}>
          {toast.message}
        </div>
      )}

      {/* View Toggle Tabs */}
      <div className="page-tabs">
        <button
          className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
          style={viewMode === 'list' ? { color: POS_GOLD, borderBottomColor: POS_GOLD } : {}}
        >
          📋 List View
        </button>
        <button
          className={`page-tab ${viewMode === 'card' ? 'active' : ''}`}
          onClick={() => setViewMode('card')}
          style={viewMode === 'card' ? { color: POS_GOLD, borderBottomColor: POS_GOLD } : {}}
        >
          🗂️ Card View
        </button>
        <button
          className={`page-tab ${viewMode === 'matrix' ? 'active' : ''}`}
          onClick={() => setViewMode('matrix')}
          style={viewMode === 'matrix' ? { color: POS_GOLD, borderBottomColor: POS_GOLD } : {}}
        >
          📊 Matrix View
        </button>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <DataTable
          columns={columns}
          data={serviceRequests}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search requests..."
          searchKeys={['notes', 'requestedDate']}
          filters={[
            {
              key: 'status',
              label: 'Status',
              options: statusOptions
            }
          ]}
          defaultSort={{ key: 'requestedDate', direction: 'desc' }}
          headerActions={{
            primary: canCreate && selectedPropertyId ? {
              label: isHotelManager ? '+ Request' : '+ Booking',
              onClick: openRoomSelector,
            } : undefined,
            onSync: () => { loadServiceRequests(); }
          }}
          actions={(row) => (
            <>
              <span className="action-icon" onClick={() => setViewingRequest(row)} title="View">👁️</span>
              {row.status === 'pending' && canConfirm && (
                <span className="action-icon" onClick={() => handleStatusUpdate(row.id, 'confirmed')} title="Confirm" style={{ color: 'var(--color-info)' }}>✓</span>
              )}
              {row.status === 'confirmed' && canComplete && (
                <span className="action-icon action-icon--success" onClick={() => handleStatusUpdate(row.id, 'completed')} title="Complete">✓✓</span>
              )}
            </>
          )}
          pageSize={20}
          emptyMessage={selectedPropertyId ? "No service requests found" : "Select a property to view service requests"}
        />
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <div className="card">
          <div className="list-toolbar">
            <div className="list-toolbar-left">
              <SearchSelect
                width="inline"
                options={[
                  { value: '', label: 'Property: All' },
                  ...userProperties.map(p => ({ value: p.id, label: p.property_name })),
                ]}
                value={selectedPropertyId}
                onChange={setSelectedPropertyId}
              />
            </div>
            <div className="list-toolbar-right">
              {canCreate && selectedPropertyId && (
                <button
                  className="btn btn-primary"
                  style={{ background: POS_GOLD, border: 'none' }}
                  onClick={openRoomSelector}
                >
                  + {isHotelManager ? 'Request Service' : 'New Booking'}
                </button>
              )}
            </div>
          </div>
          {selectedPropertyId ? renderCardView() : (
            <EmptyState message="Select a property to view service requests" />
          )}
        </div>
      )}

      {/* Matrix View */}
      {viewMode === 'matrix' && (
        <div className="card">
          <div className="list-toolbar">
            <div className="list-toolbar-left">
              <SearchSelect
                width="inline"
                options={[
                  { value: '', label: 'Property: All' },
                  ...userProperties.map(p => ({ value: p.id, label: p.property_name })),
                ]}
                value={selectedPropertyId}
                onChange={setSelectedPropertyId}
              />
            </div>
          </div>
          {selectedPropertyId ? renderMatrixView() : (
            <EmptyState message="Select a property to view service matrix" />
          )}
        </div>
      )}

      {/* View Request Modal */}
      {viewingRequest && (
        <div className="modal-overlay" onClick={() => setViewingRequest(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: `linear-gradient(135deg, ${POS_GOLD} 0%, ${POS_GOLD_DARK} 100%)`, color: '#fff' }}>
              <h2 className="modal-title">Service Request Details</h2>
              <button className="modal-close" onClick={() => setViewingRequest(null)} style={{ color: 'var(--color-white)', background: 'rgba(255,255,255,0.2)' }}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="view-section">
                <div className="view-grid">
                  <div className="view-row">
                    <span className="view-label">Date</span>
                    <span>{viewingRequest.requestedDate}</span>
                  </div>
                  <div className="view-row">
                    <span className="view-label">Time Slot</span>
                    <span>{viewingRequest.time_slot || '-'}</span>
                  </div>
                  <div className="view-row">
                    <span className="view-label">Rooms</span>
                    <span>{getRoomNumbers(viewingRequest.room_ids)}</span>
                  </div>
                  <div className="view-row">
                    <span className="view-label">Status</span>
                    <StatusBadge bg={(STATUS_CONFIG as Record<string, any>)[viewingRequest.status]?.bg} color={(STATUS_CONFIG as Record<string, any>)[viewingRequest.status]?.color}>
                      {(STATUS_CONFIG as Record<string, any>)[viewingRequest.status]?.label}
                    </StatusBadge>
                  </div>
                  {viewingRequest.notes && (
                    <div className="view-row">
                      <span className="view-label">Notes</span>
                      <span>{viewingRequest.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {viewingRequest.status === 'pending' && canConfirm && (
                <button className="btn btn-primary" onClick={() => { handleStatusUpdate(viewingRequest.id, 'confirmed'); setViewingRequest(null); }}>
                  Confirm Booking
                </button>
              )}
              {viewingRequest.status === 'confirmed' && canComplete && (
                <button className="btn btn-success" onClick={() => { handleStatusUpdate(viewingRequest.id, 'completed'); setViewingRequest(null); }}>
                  Mark Complete
                </button>
              )}
              {['pending', 'confirmed'].includes(viewingRequest.status) && canCancel && (
                <button className="btn btn-danger" onClick={() => { handleStatusUpdate(viewingRequest.id, 'cancelled'); setViewingRequest(null); }}>
                  Cancel {isHotelManager ? 'Request' : 'Booking'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setViewingRequest(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Room Selector Modal */}
      {showRoomSelector && (
        <div className="modal-overlay" onClick={() => setShowRoomSelector(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header" style={{ background: `linear-gradient(135deg, ${POS_GOLD} 0%, ${POS_GOLD_DARK} 100%)`, color: '#fff' }}>
              <h2 className="modal-title">Select Rooms for Service</h2>
              <button className="modal-close" onClick={() => setShowRoomSelector(false)} style={{ color: 'var(--color-white)', background: 'rgba(255,255,255,0.2)' }}>&times;</button>
            </div>
            <div className="flex" style={{ minHeight: '400px' }}>
              {/* Left Panel - Available Rooms */}
              <div className="flex-1 flex-column" style={{ borderRight: '1px solid var(--color-border)', display: 'flex' }}>
                <div className="flex-between" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>
                  <strong>Available Rooms</strong>
                  <select
                    className="form-input"
                    style={{ width: 'auto', padding: '6px 12px', fontSize: 'var(--text-sm)' }}
                    value={roomFilterStatus}
                    onChange={e => setRoomFilterStatus(e.target.value)}
                  >
                    <option value="all">All Available</option>
                    <option value="pending">Not Serviced</option>
                    <option value="due-soon">Due Soon</option>
                    <option value="overdue">Overdue</option>
                    <option value="certified">Certified</option>
                  </select>
                </div>
                <div style={{ padding: '1rem', overflowY: 'auto', flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '10px', alignContent: 'start' }}>
                  {filteredRoomsForSelector.map(room => {
                    const status = getRoomCurrentStatus(room);
                    const isSelected = selectedRoomIds.has(room.id);
                    return (
                      <div
                        key={room.id}
                        onClick={() => toggleRoomSelection(room.id)}
                        className="relative rounded-lg"
                        style={{
                          padding: '12px',
                          border: `2px solid ${isSelected ? POS_GOLD : 'var(--color-border)'}`,
                          cursor: 'pointer',
                          textAlign: 'center',
                          background: isSelected ? `${POS_GOLD}15` : '#fff'
                        }}
                      >
                        <div style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: '4px' }}>{room.room_number}</div>
                        <div className="text-light" style={{ fontSize: 'var(--text-xs)' }}>{room.room_type || 'Standard'}</div>
                        <div className="text-light" style={{ fontSize: 'var(--text-2xs)', marginBottom: '8px' }}>{room.bed_count || 1} bed{(room.bed_count || 1) > 1 ? 's' : ''}</div>
                        <span className="inline-block fw-600" style={{
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-lg)',
                          fontSize: 'var(--text-2xs)',
                          color: 'var(--color-white)',
                          background: SERVICE_STATUS_CONFIG[status]?.color || 'var(--color-border)'
                        }}>
                          {SERVICE_STATUS_CONFIG[status]?.label || status}
                        </span>
                        {isSelected && (
                          <span className="absolute flex-center fw-bold" style={{
                            top: '6px',
                            right: '6px',
                            width: '18px',
                            height: '18px',
                            background: POS_GOLD,
                            color: 'var(--color-white)',
                            borderRadius: '50%',
                            display: 'flex',
                            fontSize: 'var(--text-xs)'
                          }}>✓</span>
                        )}
                      </div>
                    );
                  })}
                  {filteredRoomsForSelector.length === 0 && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <EmptyState message="No rooms available with this filter" />
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel - Selected Rooms */}
              <div className="flex-column" style={{ width: '280px', display: 'flex', background: 'var(--color-bg-secondary)' }}>
                <div className="flex-between" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', background: 'var(--color-white)' }}>
                  <strong>Selected ({selectedRoomIds.size})</strong>
                  {selectedRoomIds.size > 0 && (
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 'var(--text-xs)' }} onClick={() => setSelectedRoomIds(new Set())}>Clear All</button>
                  )}
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                  {selectedRoomIds.size === 0 ? (
                    <EmptyState message="Click rooms to add them" />
                  ) : (
                    Array.from(selectedRoomIds).map(roomId => {
                      const room = rooms.find(r => r.id === roomId);
                      if (!room) return null;
                      return (
                        <div key={roomId} className="flex-between rounded-sm" style={{ padding: '10px 12px', background: 'var(--color-white)', marginBottom: '8px', border: '1px solid var(--color-border)' }}>
                          <div>
                            <div className="fw-600" style={{ fontSize: 'var(--text-md)' }}>Room {room.room_number}</div>
                            <div className="text-light" style={{ fontSize: 'var(--text-xs)' }}>{room.room_type || 'Standard'} • {room.bed_count || 1} bed{(room.bed_count || 1) > 1 ? 's' : ''}</div>
                          </div>
                          <button
                            onClick={() => toggleRoomSelection(roomId)}
                            className="flex-center"
                            style={{ width: '24px', height: '24px', border: 'none', background: 'var(--color-error-bg)', color: 'var(--color-error)', borderRadius: '50%', cursor: 'pointer', fontSize: 'var(--text-xl)', display: 'flex' }}
                          >×</button>
                        </div>
                      );
                    })
                  )}
                </div>
                {selectedRoomIds.size > 0 && (
                  <div style={{ padding: '1rem', borderTop: '1px solid var(--color-border)', background: 'var(--color-white)' }}>
                    <div className="flex-between text-light" style={{ fontSize: 'var(--text-md)', marginBottom: '4px' }}>
                      <span>Total Rooms:</span>
                      <strong>{selectedRoomsTotals.rooms}</strong>
                    </div>
                    <div className="flex-between text-light" style={{ fontSize: 'var(--text-md)' }}>
                      <span>Total Beds:</span>
                      <strong>{selectedRoomsTotals.beds}</strong>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRoomSelector(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: POS_GOLD, border: 'none', opacity: selectedRoomIds.size === 0 ? 0.5 : 1 }}
                onClick={proceedToBooking}
                disabled={selectedRoomIds.size === 0}
              >
                Proceed to Booking →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Booking Modal */}
      {showNewBookingModal && (
        <div className="modal-overlay" onClick={() => setShowNewBookingModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: `linear-gradient(135deg, ${POS_GOLD} 0%, ${POS_GOLD_DARK} 100%)`, color: '#fff' }}>
              <h2 className="modal-title">New Service Booking</h2>
              <button className="modal-close" onClick={() => setShowNewBookingModal(false)} style={{ color: 'var(--color-white)', background: 'rgba(255,255,255,0.2)' }}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={bookingForm.requested_date}
                  onChange={e => setBookingForm(prev => ({ ...prev, requested_date: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Time Slot</label>
                <select
                  className="form-input"
                  value={bookingForm.time_slot}
                  onChange={e => setBookingForm(prev => ({ ...prev, time_slot: e.target.value }))}
                >
                  <option value="">Select time slot</option>
                  <option value="morning">Morning (8am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="full-day">Full Day</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Selected Rooms</label>
                <div className="flex flex-wrap gap-sm rounded-lg mb-sm" style={{ padding: '12px', background: 'var(--color-bg-secondary)' }}>
                  {bookingForm.room_ids.map(roomId => {
                    const room = rooms.find(r => r.id === roomId);
                    return room ? (
                      <span key={roomId} className="inline-block fw-600 fs-xs" style={{ padding: '4px 10px', background: POS_GOLD, color: 'var(--color-white)', borderRadius: 'var(--radius-lg)' }}>
                        {room.room_number}
                      </span>
                    ) : null;
                  })}
                </div>
                <div className="text-light fs-xs" style={{ textAlign: 'right' }}>
                  {bookingForm.room_ids.length} room{bookingForm.room_ids.length !== 1 ? 's' : ''} • {rooms.filter(r => bookingForm.room_ids.includes(r.id)).reduce((sum, r) => sum + (r.bed_count || 1), 0)} beds
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  value={bookingForm.notes}
                  onChange={e => setBookingForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Add any special notes..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNewBookingModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: POS_GOLD, border: 'none' }} onClick={handleCreateBooking}>
                Create Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default POSServiceManagement;
